import {
  ChangeDetectorRef,
  Component,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AiOnboardingService, AiOnboardingStep } from 'src/app/services/ai-onboarding.service';
import { AiQuotaStatus, AiService } from 'src/app/services/ai.service';

/** Steps drawn by this component; the answer step is a banner inside the assistant. */
type VisibleStep = 'search' | 'quota';

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Breathing room between the target and the edge of the spotlight. */
const SPOTLIGHT_PADDING = 8;
/** Space between the spotlight and the bubble, caret included. */
const BUBBLE_GAP = 18;
/** Room the bubble needs below the target before it moves above it. */
const BUBBLE_MIN_SPACE = 340;
/** How long the counter step waits for its target before giving up. */
const TARGET_TIMEOUT_MS = 3000;

const TARGET_SELECTORS: Record<VisibleStep, string[]> = {
  search: ['[data-ai-onboarding="search"]'],
  quota: ['[data-ai-onboarding="quota"]'],
};

/**
 * The guided tour's dark layer, spotlight and bubble. It follows the element
 * each step points at (measured on screen, re-measured while shown) and sits
 * above the assistant overlay so the second step can point inside it.
 */
@Component({
  selector: 'mvp-ai-onboarding',
  templateUrl: './ai-onboarding.component.html',
  styleUrls: ['./ai-onboarding.component.scss'],
})
export class AiOnboardingComponent implements OnInit, OnDestroy {
  step: VisibleStep | null = null;
  spotlight?: Box;
  /** The counter bubble opened on demand: no step header, no tour side effects. */
  standalone = false;
  bubblePlacement: 'below' | 'above' = 'below';
  bubbleTop = 0;
  bubbleBottom = 0;
  caretLeft = 40;
  quota?: AiQuotaStatus;

  private stateSubscription?: Subscription;
  private quotaSubscription?: Subscription;
  private measureTimer?: ReturnType<typeof setInterval>;
  private targetMissingSince?: number;

  constructor(
    private aiOnboardingService: AiOnboardingService,
    private aiService: AiService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {}

  ngOnInit() {
    this.aiOnboardingService.init();
    this.quotaSubscription = this.aiService.quota$.subscribe((quota) => {
      this.quota = quota;
    });
    this.stateSubscription = this.aiOnboardingService.state$.subscribe(({ step, standalone }) => {
      this.standalone = !!standalone;
      this.showStep(step);
    });
  }

  ngOnDestroy() {
    this.stateSubscription?.unsubscribe();
    this.quotaSubscription?.unsubscribe();
    this.stopMeasuring();
  }

  @HostListener('window:resize')
  onResize() {
    this.measure();
  }

  get stepNumber(): number {
    switch (this.step) {
      case 'search':
        return 1;
      case 'quota':
        return 3;
      default:
        return 0;
    }
  }

  get totalSteps(): number {
    return this.aiOnboardingService.totalSteps;
  }

  get progressDots(): number[] {
    return Array.from({ length: this.totalSteps }, (_, index) => index + 1);
  }

  get sampleQuestion(): string {
    return AiOnboardingService.SAMPLE_QUESTION;
  }

  /** "20 questions par mois" needs a known cap; the copy adapts when there is none. */
  get quotaLimit(): number | null {
    return typeof this.quota?.limit === 'number' ? this.quota.limit : null;
  }

  get quotaResetsAt(): string | undefined {
    return this.quota?.resets_at || undefined;
  }

  skip() {
    this.aiOnboardingService.finish();
  }

  testQuestion() {
    this.aiOnboardingService.testQuestion();
  }

  /** "Poser une autre question": the tour ends and the bar is ready to type in. */
  askOwnQuestion() {
    this.aiOnboardingService.finish();
    setTimeout(() => {
      document.querySelector<HTMLInputElement>('[data-ai-onboarding="search"] input')?.focus();
    }, 50);
  }

  closeQuota() {
    this.aiOnboardingService.closeQuotaStep();
  }

  private showStep(step: AiOnboardingStep | null) {
    const visible = step === 'search' || step === 'quota' ? step : null;
    if (visible === this.step) {
      return;
    }

    this.step = visible;
    this.spotlight = undefined;
    this.targetMissingSince = undefined;
    this.stopMeasuring();

    if (!visible) {
      this.cdr.detectChanges();
      return;
    }

    this.measure();
    // The target may still be rendering (answer card, quota pill): keep
    // following it while the step is shown. Outside Angular so the polling
    // itself does not trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.measureTimer = setInterval(() => this.measure(), 250);
    });
  }

  private stopMeasuring() {
    if (this.measureTimer) {
      clearInterval(this.measureTimer);
      this.measureTimer = undefined;
    }
  }

  private findTarget(): HTMLElement | null {
    if (!this.step || typeof document === 'undefined') {
      return null;
    }

    for (const selector of TARGET_SELECTORS[this.step]) {
      const matches = document.querySelectorAll<HTMLElement>(selector);
      // The last match is the latest answer in the thread.
      const target = matches.length ? matches[matches.length - 1] : null;
      if (target && target.offsetParent !== null) {
        return target;
      }
    }

    return null;
  }

  private measure() {
    if (!this.step) {
      return;
    }

    const target = this.findTarget();
    if (!target) {
      this.handleMissingTarget();
      return;
    }
    this.targetMissingSince = undefined;

    const rect = target.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spotlight: Box = {
      top: Math.max(0, rect.top - SPOTLIGHT_PADDING),
      left: Math.max(0, rect.left - SPOTLIGHT_PADDING),
      width: Math.min(viewportWidth, rect.width + SPOTLIGHT_PADDING * 2),
      height: rect.height + SPOTLIGHT_PADDING * 2,
    };

    const spaceBelow = viewportHeight - (spotlight.top + spotlight.height);
    const placement: 'below' | 'above' = spaceBelow >= BUBBLE_MIN_SPACE || spotlight.top < BUBBLE_MIN_SPACE
      ? 'below'
      : 'above';
    const bubbleTop = spotlight.top + spotlight.height + BUBBLE_GAP;
    const bubbleBottom = viewportHeight - spotlight.top + BUBBLE_GAP;
    // The caret points at the middle of the target, kept inside the bubble.
    const bubbleLeft = 16;
    const bubbleWidth = Math.min(viewportWidth - 32, 480);
    const caretLeft = Math.min(
      bubbleWidth - 36,
      Math.max(24, rect.left + rect.width / 2 - bubbleLeft - 8),
    );

    const changed = !this.spotlight
      || this.spotlight.top !== spotlight.top
      || this.spotlight.left !== spotlight.left
      || this.spotlight.width !== spotlight.width
      || this.spotlight.height !== spotlight.height
      || this.bubblePlacement !== placement
      || this.caretLeft !== caretLeft;

    if (!changed) {
      return;
    }

    this.zone.run(() => {
      this.spotlight = spotlight;
      this.bubblePlacement = placement;
      this.bubbleTop = bubbleTop;
      this.bubbleBottom = bubbleBottom;
      this.caretLeft = caretLeft;
      this.cdr.detectChanges();
    });
  }

  /**
   * No element to point at, so nothing is drawn. The first step simply waits
   * for the home page to be on screen (a page transition can hide it for a
   * while). The counter step gives up after a short grace period: the user
   * left the home page, or the counter is gone (quota no longer metered).
   */
  private handleMissingTarget() {
    const now = Date.now();
    if (!this.targetMissingSince) {
      this.targetMissingSince = now;
    }

    if (this.spotlight) {
      this.zone.run(() => {
        this.spotlight = undefined;
        this.cdr.detectChanges();
      });
    }

    if (this.step === 'quota' && now - this.targetMissingSince > TARGET_TIMEOUT_MS) {
      this.zone.run(() => this.aiOnboardingService.closeQuotaStep());
    }
  }
}
